import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import clsx from 'clsx'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import { Placement, ZIndex } from 'shared/ui/types'

import { ClientOnlyPortal } from '../primitives/ClientOnlyPortal'

import {
  calculateAvailableSpace,
  calculatePositionForPlacement,
  doesPlacementFit,
  findBestPlacement,
  getBasePosition,
  getTriggerRect
} from './helpers'

import styles from './Tooltip.module.scss'

const BLUR_DELAY = 100
const ESTIMATED_HEIGHT = 32
const LEAVE_DELAY = 100
const TOOLTIP_OPEN_EVENT = 'tooltip.open'

interface CustomTooltipProps {
  id: string
  title: string | ReactNode
  disableHoverListener?: boolean
  children: ReactNode
  className?: string
  open?: boolean
  onClose?: () => void
  onOpen?: () => void
  placement?: Placement
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onClick?: () => void
  disableFocusListener?: boolean
  disableTouchListener?: boolean
  disableInteractive?: boolean
}

const Tooltip: React.FC<CustomTooltipProps> = ({
  id: tooltipId,
  title,
  disableHoverListener = false,
  children,
  className,
  open: controlledOpen,
  onClose,
  onOpen,
  placement = 'top',
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onClick,
  disableFocusListener = false,
  disableTouchListener = false,
  disableInteractive = false
}) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const leaveTimeoutRef = useRef<number>(0)

  // Respect user's motion preferences
  const leaveDelay = useMemo(() => {
    if (typeof window === 'undefined') return LEAVE_DELAY

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : LEAVE_DELAY
  }, [])

  const isControlled = controlledOpen !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = isControlled ? controlledOpen : internalOpen
  const hasContent = !(
    title === null ||
    title === undefined ||
    title === false ||
    title === ''
  )
  const internalOverlayId = useId()
  const overlayStackId = `${tooltipId}-${internalOverlayId}`

  const { register, unregister, isTop, getZIndex } = useOverlayStack()

  // Register/unregister tooltip in overlay stack during lifecycle
  useEffect(() => {
    if (!isOpen || !hasContent) return undefined
    register({ id: overlayStackId, type: 'tooltip' })

    return () => unregister(overlayStackId)
  }, [isOpen, hasContent, overlayStackId, register, unregister])

  const handleOpen = useCallback(() => {
    if (!hasContent) return

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }

    const openTooltip = () => {
      if (!isControlled) {
        setInternalOpen(true)
      }
      onOpen?.()
    }

    openTooltip()
  }, [hasContent, isControlled, onOpen])

  const handleClose = useCallback(() => {
    const closeTooltip = () => {
      if (!isControlled) {
        setInternalOpen(false)
      }
      onClose?.()
    }

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }

    if (leaveDelay > 0) {
      leaveTimeoutRef.current = setTimeout(
        closeTooltip,
        LEAVE_DELAY
      ) as unknown as number
    } else {
      closeTooltip()
    }
  }, [isControlled, onClose, leaveDelay])

  const closeImmediately = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }
    setInternalOpen(false)
    onClose?.()
  }, [onClose])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isTop(overlayStackId)) {
      handleClose()
    }
  }

  // Touch taps emit emulated mouse events with no matching leave, so hover is mouse/pen only
  const handlePointerEnter = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === 'touch') return

      if (!disableHoverListener) {
        handleOpen()
      }
      onMouseEnter?.()
    },
    [disableHoverListener, handleOpen, onMouseEnter]
  )

  const handlePointerLeave = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === 'touch') return

      if (!disableHoverListener) {
        handleClose()
      }
      onMouseLeave?.()
    },
    [disableHoverListener, handleClose, onMouseLeave]
  )

  const handleFocus = () => {
    if (!disableFocusListener) {
      handleOpen()
    }
    onFocus?.()
  }

  const handleBlur = () => {
    const isTriggerFocused = triggerRef.current?.contains(
      document.activeElement
    )

    setTimeout(() => {
      if (!disableFocusListener && !isTriggerFocused) {
        handleClose()
      }
    }, BLUR_DELAY)
    onBlur?.()
  }

  const handleClick = () => {
    handleOpen()
    onClick?.()
  }

  const handleClickOutside = useCallback(
    (event: Event) => {
      if (!isOpen) return

      const target = event.target as Node
      const triggerElement = triggerRef.current
      const tooltipElement = tooltipRef.current

      const isOutsideTrigger =
        !triggerElement || !triggerElement.contains(target)

      const isOutsideTooltip =
        !tooltipElement || !tooltipElement.contains(target)

      if (isOutsideTrigger && isOutsideTooltip) {
        handleClose()
      }
    },
    [isOpen, handleClose]
  )

  useEffect(() => {
    // Capture phase so stopPropagation can't block it; touchstart covers taps that skip pointerdown
    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside, true)
      document.addEventListener('touchstart', handleClickOutside, {
        capture: true,
        passive: true
      })

      return () => {
        document.removeEventListener('pointerdown', handleClickOutside, true)
        document.removeEventListener('touchstart', handleClickOutside, true)
      }
    }

    return undefined
  }, [isOpen, handleClickOutside])

  useEffect(() => {
    if (isOpen) {
      document.dispatchEvent(
        new CustomEvent(TOOLTIP_OPEN_EVENT, { detail: overlayStackId })
      )
    }
  }, [isOpen, overlayStackId])

  useEffect(() => {
    // Only one uncontrolled tooltip open at a time
    if (!isOpen || isControlled) return undefined

    const handleOtherTooltipOpen = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== overlayStackId) {
        closeImmediately()
      }
    }

    document.addEventListener(TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen)

    return () => {
      document.removeEventListener(TOOLTIP_OPEN_EVENT, handleOtherTooltipOpen)
    }
  }, [isOpen, isControlled, overlayStackId, closeImmediately])

  useEffect(() => {
    // Position is computed once on open, so close when an ancestor of the trigger scrolls
    if (!isOpen || isControlled) return undefined

    const handleScroll = (event: Event) => {
      const target = event.target
      const triggerElement = triggerRef.current

      if (
        target === document ||
        (target instanceof Node &&
          triggerElement &&
          target.contains(triggerElement))
      ) {
        closeImmediately()
      }
    }

    window.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true
    })

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, isControlled, closeImmediately])

  useEffect(() => {
    // Touch event listener and cleanup
    if (!disableTouchListener) {
      const handleTouchStart = () => {
        handleOpen()
      }

      const triggerElement = triggerRef.current

      if (triggerElement) {
        triggerElement.addEventListener('touchstart', handleTouchStart, {
          passive: true
        })

        return () => {
          triggerElement.removeEventListener('touchstart', handleTouchStart)
        }
      }
    }

    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [disableTouchListener, handleOpen])

  const getOptimalPlacement = useCallback((): Placement => {
    if (!triggerRef.current) return placement

    // When using display: contents, calculate based on first child element
    const triggerRect = getTriggerRect(triggerRef.current)
    const availableSpace = calculateAvailableSpace(triggerRect)

    const tooltipHeight =
      tooltipRef?.current?.getBoundingClientRect()?.height || ESTIMATED_HEIGHT

    // Prefer original placement if it fits
    if (doesPlacementFit(tooltipHeight, availableSpace)) {
      return placement
    }

    // Otherwise find the best available placement
    return findBestPlacement(availableSpace)
  }, [placement])

  const actualPlacement = useMemo(() => {
    return isOpen ? getOptimalPlacement() : placement
  }, [isOpen, getOptimalPlacement, placement])

  const tooltipPosition = useMemo(() => {
    if (!isOpen || !triggerRef.current)
      return { top: 0, left: 0, transform: '' }

    const triggerRect = getTriggerRect(triggerRef.current)
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const basePosition = getBasePosition(triggerRect)
    const tooltipHeight =
      tooltipRef?.current?.getBoundingClientRect()?.height || ESTIMATED_HEIGHT

    return calculatePositionForPlacement({
      targetPlacement: actualPlacement,
      triggerRect,
      basePosition,
      viewportWidth,
      viewportHeight,
      tooltipHeight: tooltipHeight
    })
  }, [isOpen, actualPlacement])

  const zIndex = useMemo(
    () => getZIndex(overlayStackId) || ZIndex.TOOLTIP,
    [getZIndex, overlayStackId]
  )

  return (
    <div
      className={clsx(styles.container, className)}
      style={{ display: 'contents' }}
    >
      <div
        ref={triggerRef}
        aria-describedby={isOpen ? tooltipId : undefined}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{ display: 'contents' }}
      >
        {children}
      </div>
      {hasContent && (
        <ClientOnlyPortal selector='#overlay-portal'>
          <div
            ref={tooltipRef}
            id={tooltipId}
            role='tooltip'
            aria-live='polite'
            aria-atomic='true'
            className={clsx(
              styles.content,
              { [styles.interactive]: !disableInteractive },
              className
            )}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            style={{
              position: 'absolute',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: tooltipPosition.transform,
              zIndex: zIndex,
              display: isOpen ? 'block' : 'none'
            }}
          >
            {title}
          </div>
        </ClientOnlyPortal>
      )}
    </div>
  )
}

export default memo(Tooltip)
